import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { getAuthToken } from '../utils/auth';
import BASE_URL from '../utils/api';

export default function PopupImportacaoEmMassa({
  titulo,
  nomeArquivo,
  nomeAba,
  colunas,
  camposObrigatorios,
  exemplos,
  instrucoes,
  endpoint,
  criarPayload,
  onFechar,
  onSucesso
}) {
  const [arquivo, setArquivo] = useState(null);
  const [processando, setProcessando] = useState(false);
  const [progresso, setProgresso] = useState({ atual: 0, total: 0 });
  const [resultados, setResultados] = useState([]);
  const [erroArquivo, setErroArquivo] = useState('');
  const inputRef = useRef(null);
  const cancelarRef = useRef(false);

  const baixarTemplate = (formato) => {
    const worksheet = XLSX.utils.json_to_sheet(exemplos, { header: colunas });
    worksheet['!cols'] = colunas.map(coluna => ({ wch: Math.max(coluna.length + 3, 16) }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, nomeAba);
    XLSX.writeFile(workbook, `${nomeArquivo}.${formato}`);
  };

  const handleArquivo = (event) => {
    const novoArquivo = event.target.files?.[0];
    setErroArquivo('');
    setResultados([]);
    if (!novoArquivo) return;

    const extensao = novoArquivo.name.split('.').pop()?.toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(extensao)) {
      setArquivo(null);
      setErroArquivo('Utilize um arquivo Excel (.xlsx ou .xls) ou CSV (.csv).');
      return;
    }
    setArquivo(novoArquivo);
  };

  const lerLinhas = async () => {
    const buffer = await arquivo.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    return XLSX.utils.sheet_to_json(worksheet, { defval: '' });
  };

  const iniciarImportacao = async () => {
    if (!arquivo) return;

    setErroArquivo('');
    setResultados([]);
    cancelarRef.current = false;

    let linhas;
    try {
      linhas = await lerLinhas();
    } catch {
      setErroArquivo('Não foi possível ler o arquivo. Verifique o formato e tente novamente.');
      return;
    }

    if (linhas.length === 0) {
      setErroArquivo('O arquivo não contém linhas para importar.');
      return;
    }

    const colunasArquivo = Object.keys(linhas[0]);
    const colunasAusentes = colunas.filter(coluna => !colunasArquivo.includes(coluna));
    if (colunasAusentes.length > 0) {
      setErroArquivo(`Colunas ausentes: ${colunasAusentes.join(', ')}.`);
      return;
    }

    setProcessando(true);
    setProgresso({ atual: 0, total: linhas.length });
    const novosResultados = [];
    const token = getAuthToken();

    for (let indice = 0; indice < linhas.length; indice += 1) {
      if (cancelarRef.current) break;

      const linha = linhas[indice];
      const numeroLinha = indice + 2;
      const faltantes = camposObrigatorios.filter(campo => !String(linha[campo] ?? '').trim());
      const nome = String(linha[colunas[0]] ?? `Linha ${numeroLinha}`).trim();

      if (faltantes.length > 0) {
        novosResultados.push({ linha: numeroLinha, nome, sucesso: false, mensagem: `Campos obrigatórios ausentes: ${faltantes.join(', ')}` });
      } else {
        try {
          const response = await fetch(`${BASE_URL}/${endpoint}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: JSON.stringify(criarPayload(linha))
          });
          const mensagem = response.ok ? 'Cadastrado com sucesso.' : (await response.text()) || `Erro ${response.status}.`;
          novosResultados.push({ linha: numeroLinha, nome, sucesso: response.ok, mensagem });
        } catch (error) {
          novosResultados.push({ linha: numeroLinha, nome, sucesso: false, mensagem: error.message || 'Não foi possível conectar à API.' });
        }
      }

      setProgresso({ atual: indice + 1, total: linhas.length });
      setResultados([...novosResultados]);
    }

    setProcessando(false);
    if (novosResultados.some(resultado => resultado.sucesso)) onSucesso?.();
  };

  const sucessos = resultados.filter(resultado => resultado.sucesso).length;
  const erros = resultados.length - sucessos;

  return (
    <div className="modal-overlay" onClick={processando ? undefined : onFechar} style={{ position: 'fixed', inset: 0, zIndex: 95000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0, 0, 0, 0.52)' }}>
      <div onClick={event => event.stopPropagation()} style={{ width: 700, maxWidth: '96vw', maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: 8, boxShadow: '0 8px 40px rgba(0,0,0,.28)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', background: 'rgb(235, 133, 0)', color: '#fff' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20 }}>Cadastro em Massa de {titulo}</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13 }}>Importe registros por planilha Excel ou arquivo CSV.</p>
          </div>
          {!processando && <button type="button" onClick={onFechar} aria-label="Fechar" style={{ border: 0, background: 'transparent', color: '#fff', fontSize: 26, cursor: 'pointer' }}>x</button>}
        </div>
        <div style={{ padding: 24 }}>
          <p style={{ marginTop: 0, color: '#555' }}>Baixe o modelo, preencha uma linha por registro e envie o arquivo. Campos obrigatórios: <strong>{camposObrigatorios.join(', ')}</strong>.</p>
          <ul style={{ color: '#555', fontSize: 13 }}>{instrucoes.map(instrucao => <li key={instrucao}>{instrucao}</li>)}</ul>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, margin: '18px 0' }}>
            <button type="button" onClick={() => baixarTemplate('xlsx')} disabled={processando} style={{ padding: '10px 16px', border: 0, borderRadius: 6, background: '#2e7d32', color: '#fff', cursor: 'pointer' }}>Baixar modelo Excel</button>
            <button type="button" onClick={() => baixarTemplate('csv')} disabled={processando} style={{ padding: '10px 16px', border: '1px solid #2e7d32', borderRadius: 6, background: '#fff', color: '#2e7d32', cursor: 'pointer' }}>Baixar modelo CSV</button>
          </div>
          <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleArquivo} disabled={processando} />
          {arquivo && <p style={{ color: '#555', fontSize: 13 }}>Arquivo selecionado: {arquivo.name}</p>}
          {erroArquivo && <p style={{ color: '#b00020' }}>{erroArquivo}</p>}
          {processando && <p style={{ color: '#555' }}>Processando {progresso.atual} de {progresso.total}...</p>}
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button type="button" onClick={iniciarImportacao} disabled={!arquivo || processando} style={{ padding: '11px 20px', border: 0, borderRadius: 6, background: '#2e7d32', color: '#fff', cursor: 'pointer' }}>Importar</button>
            {processando && <button type="button" onClick={() => { cancelarRef.current = true; }} style={{ padding: '11px 20px', border: 0, borderRadius: 6, background: '#d32f2f', color: '#fff', cursor: 'pointer' }}>Cancelar importação</button>}
          </div>
          {resultados.length > 0 && <div style={{ marginTop: 20 }}>
            <strong>{sucessos} importados, {erros} com erro</strong>
            <div style={{ maxHeight: 220, overflowY: 'auto', marginTop: 8, border: '1px solid #ddd' }}>
              {resultados.map(resultado => <div key={resultado.linha} style={{ padding: '8px 10px', borderBottom: '1px solid #eee', color: resultado.sucesso ? '#2e7d32' : '#b00020', fontSize: 13 }}>Linha {resultado.linha} - {resultado.nome}: {resultado.mensagem}</div>)}
            </div>
          </div>}
        </div>
      </div>
    </div>
  );
}